import { FixedAsset, PaymentTaskFixedAsset } from '@/hooks/api/models';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useCurrentPaymentTask } from '@/providers/PaymentTaskProvider';
import { useCreatePaymentTaskFixedAsset } from '@/hooks/api/1160_create_payment_task_fixed_asset';
import { usePaymentTaskFixedAssetsInvalidate } from '@/hooks/api/1150_payment_task_fixed_assets';
import React, { useEffect, useState } from 'react';
import { SelectOptionType } from '@/components/element/Form/SelectOptionType';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { toastApiError } from '@/lib/toast';
import { SingleValue } from 'react-select';
import { SelectSingle } from '@/components/element/Form/SelectSingle';
import { FormGroup } from '@/components/element/Form/FormGroup';
import { useParams } from 'react-router-dom';
import { InputPrice } from '@/components/element/Form/InputPrice';
import { useMeSalesOrders } from '@/hooks/api/2470_me_sales_orders';
import { useCreateOrderAllocatedExpense } from '@/hooks/api/1193_create_order_allocated_expense';
import { useInvalidateOrderAllocatedExpenses } from '@/hooks/api/1192_order_allocated_expenses';
import { SalesOrderOutlineLogic } from '@/hooks/api/models/logics';
import { useRelatedOrders } from '@/hooks/api/0640_related_orders';

type FormInputs = {
  identifier: string;
  team_id: number;
  purchase_order_id: number;
  task_id: number;
  related_order_id: number;
  amount: number;
};

export const PaymentTaskOrderAllocatedExpenseCreateForm = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { paymentTask: task } = useCurrentPaymentTask();
  const { data: relatedOrders } = useRelatedOrders({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: Number(purchase_order_id),
  });
  const { mutate, data, isPending, error } = useCreateOrderAllocatedExpense();
  const { invalidate } = useInvalidateOrderAllocatedExpenses({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    task_id: task!.id,
  });
  const [options, setOptions] = useState<SelectOptionType[]>([]);
  const {
    control,
    setValue,
    getValues,
    reset,
    trigger,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onSubmit',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id),
      task_id: task!.id,
      amount: 0,
    },
  });

  useEffect(() => {
    if (relatedOrders)
      setOptions(
        relatedOrders.map(
          (d): SelectOptionType => ({ value: d.id, label: SalesOrderOutlineLogic.toLabel(d.sales_order_outline) })
        )
      );
  }, [relatedOrders]);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (data) {
      invalidate();
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  const onFixedAssetChange = (sv: SingleValue<SelectOptionType>) => {
    if (sv) {
      setValue('related_order_id', sv.value);
      trigger('related_order_id');
    } else {
      reset(undefined, { keepDefaultValues: true });
    }
  };

  return (
    <>
      <div className="col-12 col-md-7">
        <div className="form-group">
          <label>
            関連プロジェクト<span className="text-danger">*</span>
          </label>
          <Controller
            control={control}
            name="related_order_id"
            rules={{ required: true }}
            render={({ field: { ref, ...field } }) => (
              <SelectSingle
                options={options}
                selectedId={getValues('related_order_id')}
                onChange={onFixedAssetChange}
                error={errors.related_order_id != null}
              />
            )}
          />
        </div>
      </div>
      <div className="col-12 col-md-2">
        <FormGroup label="金額" required={true}>
          <div className="input-group">
            <Controller
              render={({ field: { onChange, value, ...field } }) => (
                <InputPrice onChange={onChange} value={value} error={errors.amount} />
              )}
              control={control}
              name="amount"
              rules={{ required: true }}
            />
          </div>
        </FormGroup>
      </div>
      <div className="col-12 col-md-3">
        <div className="form-group">
          <label>&nbsp;</label>
          <button className="btn w-100 btn-icon bg-gradient-dark" onClick={() => handleSubmit(onSubmit)()}>
            <i className="fa fa-plus"></i>追加
          </button>
        </div>
      </div>
    </>
  );
};
